import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { useAuth } from "react-oidc-context";

const UserPreferencesContext = createContext();

export const UserPreferencesProvider = ({ children }) => {
  const auth = useAuth();
  const [theme, setTheme] = useState("light");
  const [language, setLanguage] = useState("EN");
  const [isLoading, setIsLoading] = useState(true);

  const loadUserPreferences = useCallback(async () => {
    if (!auth.isAuthenticated || !auth.user) {
      setIsLoading(false);
      return;
    }

    try {
      const userId = auth.user.profile["cognito:username"];
      const response = await fetch(
        `https://x0mhcopby7.execute-api.eu-central-1.amazonaws.com/UserManagementFunction/${encodeURIComponent(
          userId
        )}/preferences`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${auth.user.access_token}`,
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.preferences) {
        setTheme(data.preferences.theme || "light");
        setLanguage(data.preferences.language || "EN");
      }
    } catch (error) {
      console.error("Error loading preferences:", error);
      setTheme("light");
      setLanguage("EN");
    } finally {
      setIsLoading(false);
    }
  }, [auth.isAuthenticated, auth.user]);

  const saveUserPreferences = useCallback(
    async (newTheme, newLanguage) => {
      if (!auth.isAuthenticated || !auth.user) return;

      try {
        const userId = auth.user.profile["cognito:username"];
        const response = await fetch(
          `https://x0mhcopby7.execute-api.eu-central-1.amazonaws.com/UserManagementFunction/${encodeURIComponent(
            userId
          )}/preferences`,
          {
            method: "PUT",
            headers: {
              Authorization: `Bearer ${auth.user.access_token}`,
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            body: JSON.stringify({
              preferences: {
                theme: newTheme,
                language: newLanguage,
              },
            }),
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        console.log("Preferences saved successfully:", data);
      } catch (error) {
        console.error("Error saving preferences:", error);
        setTheme(newTheme);
        setLanguage(newLanguage);
      }
    },
    [auth.isAuthenticated, auth.user]
  );

  const updateTheme = useCallback(
    async (newTheme) => {
      setTheme(newTheme);
      if (auth.isAuthenticated) {
        await saveUserPreferences(newTheme, language);
      }
      document.documentElement.classList.toggle("dark", newTheme === "dark");
    },
    [auth.isAuthenticated, language, saveUserPreferences]
  );

  const updateLanguage = useCallback(
    async (newLanguage) => {
      setLanguage(newLanguage);
      if (auth.isAuthenticated) {
        await saveUserPreferences(theme, newLanguage);
      }
    },
    [auth.isAuthenticated, theme, saveUserPreferences]
  );

  useEffect(() => {
    if (auth.isAuthenticated) {
      loadUserPreferences();
    } else {
      setIsLoading(false);
      setTheme("light");
      setLanguage("EN");
    }
  }, [auth.isAuthenticated, loadUserPreferences]);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", theme === "dark");
  }, [theme]);

  return (
    <UserPreferencesContext.Provider
      value={{
        theme,
        language,
        updateTheme,
        updateLanguage,
        isLoading,
      }}
    >
      {!isLoading && children}
    </UserPreferencesContext.Provider>
  );
};

export const useUserPreferences = () => {
  const context = useContext(UserPreferencesContext);
  if (context === undefined) {
    throw new Error(
      "useUserPreferences must be used within a UserPreferencesProvider"
    );
  }
  return context;
};

export default UserPreferencesContext;
